import { useEffect, useRef } from 'react';
import { SprintAPI } from '../../../utils/api';
import { getActiveWorkspaceId } from '../../../utils/api/workspaceContext';
import { queuePendingTaskAdds } from '../../../utils/pendingSprintTaskAdds';

function taskIdsOf(tasks) {
  return new Set((tasks || []).map((t) => t.id));
}

function hasNewTaskIds(prevTasks, currentTasks) {
  const prev = taskIdsOf(prevTasks);
  return (currentTasks || []).some((t) => t?.id && !prev.has(t.id));
}

function fallbackCampaignId(currentTasks) {
  return currentTasks.find((t) => t.sourceCampaign?.id)?.sourceCampaign?.id || null;
}

function campaignUpdatesFromView(currentTasks, allSprints) {
  const fallbackId = fallbackCampaignId(currentTasks);
  const tasksByCampaign = new Map();
  currentTasks.forEach((task) => {
    const campaignId = task.sourceCampaign?.id || fallbackId;
    if (!campaignId) return;
    if (!tasksByCampaign.has(campaignId)) tasksByCampaign.set(campaignId, []);
    tasksByCampaign.get(campaignId).push(task);
  });

  const updates = [];
  tasksByCampaign.forEach((campaignTasks, campaignId) => {
    const sourceCampaign = allSprints.find((s) => s.id === campaignId);
    if (!sourceCampaign) {
      console.warn(`[MultiCampaignSync] Source campaign not found: ${campaignId}`);
      return;
    }

    const cleanedTasks = campaignTasks.map((task) => {
      const {
        sourceCampaign: _sc,
        position,
        _originalPosition,
        _campaignOffset,
        ...taskWithoutViewData
      } = task;
      return taskWithoutViewData;
    });

    const MIN_X = 100;
    const minX = Math.min(...cleanedTasks.map((t) => t.x));
    const normalizeOffset = minX < MIN_X ? MIN_X - minX : 0;
    const adjustedTasks =
      normalizeOffset > 0
        ? cleanedTasks.map((task) => ({ ...task, x: task.x + normalizeOffset }))
        : cleanedTasks;

    updates.push({ campaignId, sourceCampaign, adjustedTasks });
  });
  return updates;
}

function persistCampaignUpdate(sourceCampaign, adjustedTasks, onUpdateSprint) {
  const existingTasks = Array.isArray(sourceCampaign.tasks) ? sourceCampaign.tasks : [];
  const viewTaskIds = new Set(adjustedTasks.map((t) => t.id));
  const tasksNotInView = existingTasks.filter((t) => !viewTaskIds.has(t.id));
  onUpdateSprint(
    {
      ...sourceCampaign,
      categories: sourceCampaign.categories || [],
      tasks: [...tasksNotInView, ...adjustedTasks],
    },
    { immediate: true }
  );

  if (Array.isArray(sourceCampaign.tasks)) return;

  SprintAPI.get(sourceCampaign.id, { workspaceId: getActiveWorkspaceId() })
    .then((full) => {
      if (!full?.tasks) return;
      const extra = full.tasks.filter((t) => !viewTaskIds.has(t.id));
      onUpdateSprint(
        { ...sourceCampaign, ...full, tasks: [...extra, ...adjustedTasks] },
        { immediate: true }
      );
    })
    .catch(() => {});
}

/**
 * Syncs task changes from a multi-campaign view back to their source campaigns.
 * New missions persist immediately so a refresh cannot drop them.
 */
export function useMultiCampaignSync({
  isMultiCampaignMode,
  localSprint,
  allSprints,
  onUpdateSprint,
}) {
  const prevTasksRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const pendingTasksRef = useRef(null);
  const allSprintsRef = useRef(allSprints);
  allSprintsRef.current = allSprints;
  const onUpdateRef = useRef(onUpdateSprint);
  onUpdateRef.current = onUpdateSprint;

  const flushPending = (tasks, prevTasks) => {
    const currentTasks = tasks || pendingTasksRef.current;
    pendingTasksRef.current = null;
    if (!currentTasks || !onUpdateRef.current) return;
    if (prevTasks) {
      const prevIds = new Set(prevTasks.map((t) => t.id));
      const added = currentTasks.filter((t) => t?.id && !prevIds.has(t.id));
      const fallbackId = fallbackCampaignId(currentTasks);
      const addedByCampaign = new Map();
      added.forEach((task) => {
        const campaignId = task.sourceCampaign?.id || fallbackId;
        if (!campaignId) return;
        if (!addedByCampaign.has(campaignId)) addedByCampaign.set(campaignId, []);
        addedByCampaign.get(campaignId).push(task);
      });
      const wsId = getActiveWorkspaceId();
      addedByCampaign.forEach((campaignTasks, campaignId) => {
        queuePendingTaskAdds(wsId, campaignId, campaignTasks);
      });
    }
    const updates = campaignUpdatesFromView(currentTasks, allSprintsRef.current || []);
    updates.forEach(({ sourceCampaign, adjustedTasks }) => {
      persistCampaignUpdate(sourceCampaign, adjustedTasks, onUpdateRef.current);
    });
  };

  useEffect(() => {
    if (!isMultiCampaignMode || !onUpdateSprint) return;

    const currentTasks = localSprint?.tasks || [];
    const prevTasks = prevTasksRef.current;

    if (prevTasks === null) {
      prevTasksRef.current = currentTasks;
      return;
    }

    if (prevTasks === currentTasks) return;

    const prevIds = new Set(prevTasks.map((t) => t.id));
    const currentIds = new Set(currentTasks.map((t) => t.id));
    const idsMatch =
      prevIds.size === currentIds.size && [...prevIds].every((id) => currentIds.has(id));

    const hasContentChanges = currentTasks.some((task) => {
      const prevTask = prevTasks.find((t) => t.id === task.id);
      if (!prevTask) return true;
      return (
        task.name !== prevTask.name ||
        task.status !== prevTask.status ||
        task.description !== prevTask.description ||
        task.notes !== prevTask.notes ||
        task.internalNotes !== prevTask.internalNotes ||
        JSON.stringify(task.dependsOn) !== JSON.stringify(prevTask.dependsOn) ||
        task.archived !== prevTask.archived ||
        task.answer !== prevTask.answer ||
        task.selectedOption !== prevTask.selectedOption ||
        task.nodeType !== prevTask.nodeType ||
        JSON.stringify(task.docTicks || {}) !== JSON.stringify(prevTask.docTicks || {}) ||
        JSON.stringify(task.docFiles || {}) !== JSON.stringify(prevTask.docFiles || {}) ||
        JSON.stringify(task.variableValues || []) !== JSON.stringify(prevTask.variableValues || [])
      );
    });

    const hasPositionChanges = currentTasks.some((task) => {
      const prevTask = prevTasks.find((t) => t.id === task.id);
      if (!prevTask) return false;
      return task.x !== prevTask.x || task.y !== prevTask.y;
    });

    const hasDeletedTasks = prevTasks.some(
      (pt) => !currentTasks.find((ct) => ct.id === pt.id)
    );

    if (!hasContentChanges && !hasDeletedTasks && !hasPositionChanges && idsMatch) {
      prevTasksRef.current = currentTasks;
      return;
    }

    prevTasksRef.current = currentTasks;
    pendingTasksRef.current = currentTasks;

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    if (hasNewTaskIds(prevTasks, currentTasks) || hasDeletedTasks) {
      flushPending(currentTasks, prevTasks);
      return;
    }

    syncTimeoutRef.current = setTimeout(() => {
      syncTimeoutRef.current = null;
      flushPending(currentTasks, prevTasks);
    }, 500);

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
  }, [isMultiCampaignMode, localSprint?.tasks, onUpdateSprint]);

  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      if (pendingTasksRef.current) {
        flushPending(pendingTasksRef.current);
      }
    };
  }, []);
}

export default useMultiCampaignSync;
